import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import swal from 'sweetalert';
import TaxService from '../../../services/TaxService';
import InvoiceService from '../../../services/InvoiceService';
import { PageHeader, Card, Button, Badge, Toggle, Field, Input, Select, Table, Tr, Td, EmptyState, inputClass } from '../../ui';

const TABS = [['company', 'Invoice company', 'fa-building'], ['taxes', 'Tax & charges', 'fa-percent']];

const emptyRate = { name: '', type: 'percentage', rate: '', registration_number: '', region: '', description: '', is_active: true };

const Textarea = (props) => <textarea {...props} className={`${inputClass} ${props.className || ''}`} />;

const BillingTaxSettings = () => {
  const [tab, setTab] = useState('company');
  const [company, setCompany] = useState(null);
  const [autoEnabled, setAutoEnabled] = useState(true);
  const [taxEnabled, setTaxEnabled] = useState(true);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [rateModal, setRateModal] = useState(null);

  const flash = (ok, text) => { setMsg({ ok, text }); setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, t] = await Promise.all([InvoiceService.getCompany(), TaxService.adminList()]);
      setCompany(c.company); setAutoEnabled(c.auto_enabled);
      setTaxEnabled(t.tax_enabled); setRates(t.rates);
    } catch (e) {
      flash(false, 'Failed to load billing settings');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveCompany = async () => {
    setSaving(true);
    try {
      const res = await InvoiceService.updateCompany({ ...company, auto_enabled: autoEnabled });
      setCompany(res.company); setAutoEnabled(res.auto_enabled);
      flash(true, 'Saved. Applies to the next invoice.');
    } catch (e) { flash(false, e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const toggleTax = async (v) => {
    setTaxEnabled(v);
    try { await TaxService.setEnabled(v); flash(true, v ? 'Taxes will be applied at checkout.' : 'Taxes are off — no tax added at checkout.'); }
    catch { setTaxEnabled(!v); flash(false, 'Failed to update'); }
  };

  const toggleRate = async (r, v) => {
    try {
      const updated = await TaxService.update(r.id, { is_active: v });
      setRates((list) => list.map((x) => (x.id === r.id ? updated : x)));
    } catch { flash(false, 'Failed to update rate'); }
  };

  const saveRate = async () => {
    try {
      const payload = { ...rateModal, rate: parseFloat(rateModal.rate) || 0 };
      const saved = rateModal.id ? await TaxService.update(rateModal.id, payload) : await TaxService.create(payload);
      setRates((list) => (rateModal.id ? list.map((x) => (x.id === saved.id ? saved : x)) : [...list, saved]));
      setRateModal(null);
      flash(true, 'Tax rate saved.');
    } catch (e) { flash(false, e.response?.data?.error || 'Save failed'); }
  };

  const deleteRate = (r) => swal({ title: 'Delete this rate?', text: r.name, icon: 'warning', buttons: ['Cancel', 'Delete'], dangerMode: true })
    .then(async (ok) => { if (!ok) return; await TaxService.remove(r.id); setRates((l) => l.filter((x) => x.id !== r.id)); });

  if (loading || !company) {
    return <div className="tw-py-16 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-2xl tw-text-brand" /></div>;
  }

  const set = (k) => (e) => setCompany((c) => ({ ...c, [k]: e.target.value }));

  return (
    <div className="tw-mx-auto tw-max-w-3xl">
      <PageHeader
        title="Billing & Tax"
        subtitle="Invoices are issued under the parent company (Siman Tech) — configure the company details and which taxes / charges apply."
        actions={tab === 'company' && <Button icon="fa-floppy-disk" onClick={saveCompany} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>}
      />

      {msg && (
        <div className={`tw-mb-5 tw-rounded-xl tw-border tw-px-4 tw-py-3 tw-text-sm ${msg.ok ? 'tw-border-good/30 tw-bg-good/10 tw-text-good' : 'tw-border-live/30 tw-bg-live/10 tw-text-live'}`}>{msg.text}</div>
      )}

      <div className="tw-mb-6 tw-flex tw-gap-1.5">
        {TABS.map(([id, label, icon]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium ${tab === id ? 'tw-bg-brand tw-text-white' : 'tw-bg-sunken tw-text-content-muted hover:tw-text-content'}`}>
            <i className={`fa-solid ${icon} tw-mr-1.5`} />{label}
          </button>
        ))}
      </div>

      {tab === 'company' && (
        <>
          <Card title="Parent company (appears on every invoice)" className="tw-mb-5">
            <div className="tw-space-y-4">
              <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                <Field label="Company name"><Input value={company.name} onChange={set('name')} /></Field>
                <Field label="Legal name"><Input value={company.legal_name} onChange={set('legal_name')} /></Field>
                <Field label="Billing email" hint="Invoices are sent from this address"><Input value={company.email} onChange={set('email')} /></Field>
                <Field label="Phone"><Input value={company.phone} onChange={set('phone')} /></Field>
                <Field label="Website"><Input value={company.website} onChange={set('website')} /></Field>
                <Field label="Tax / GST-HST registration #" hint="Printed in the invoice header"><Input value={company.tax_id} onChange={set('tax_id')} /></Field>
              </div>
              <Field label="Address"><Textarea rows={3} value={company.address} onChange={set('address')} /></Field>
              <Field label="Logo URL" hint="Optional — Siman Tech logo shown on the invoice"><Input value={company.logo_url} onChange={set('logo_url')} /></Field>
              <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                <Field label="Invoice number prefix"><Input value={company.prefix} onChange={set('prefix')} placeholder="SIMAN" /></Field>
                <Field label="Payment due (days)" hint="0 = due on receipt"><Input type="number" value={company.due_days} onChange={set('due_days')} /></Field>
              </div>
              <Field label="Footer note"><Textarea rows={2} value={company.footer_note} onChange={set('footer_note')} /></Field>
            </div>
          </Card>

          <Card title="Automation">
            <div className="tw-flex tw-items-center tw-justify-between">
              <div>
                <div className="tw-font-semibold tw-text-heading">Auto-issue invoice on payment</div>
                <div className="tw-text-sm tw-text-content-muted">Every successful subscription charge generates a paid invoice and emails the PDF to the customer.</div>
              </div>
              <Toggle checked={autoEnabled} onChange={setAutoEnabled} />
            </div>
          </Card>
        </>
      )}

      {tab === 'taxes' && (
        <>
          <Card className="tw-mb-5">
            <div className="tw-flex tw-items-center tw-justify-between">
              <div>
                <div className="tw-font-semibold tw-text-heading">Apply taxes &amp; charges at checkout</div>
                <div className="tw-text-sm tw-text-content-muted">Master switch. When off, no tax or handling charge is added to any payment — regardless of the rows below.</div>
              </div>
              <Toggle checked={taxEnabled} onChange={toggleTax} />
            </div>
          </Card>

          <Card
            title="Rates & charges"
            subtitle="Each row can be enabled independently. Percentage rates apply to the plan price; fixed charges add a flat amount (in cents)."
            actions={<Button size="sm" icon="fa-plus" onClick={() => setRateModal({ ...emptyRate })}>Add</Button>}
            noPad
          >
            {rates.length === 0 ? (
              <EmptyState icon="fa-percent" title="No tax rates" action={<Button icon="fa-plus" onClick={() => setRateModal({ ...emptyRate })}>Add a rate</Button>} />
            ) : (
              <Table head={['Name', 'Rate', 'Registration #', 'Active', '']}>
                {rates.map((r) => (
                  <Tr key={r.id}>
                    <Td className="tw-font-semibold tw-text-heading">{r.name}{r.region ? <span className="tw-ml-1.5 tw-text-xs tw-text-content-faint">{r.region}</span> : null}</Td>
                    <Td>{r.type === 'percentage' ? `${r.rate}%` : `$${(r.rate / 100).toFixed(2)} flat`}</Td>
                    <Td className="tw-text-content-muted">{r.registration_number || '—'}</Td>
                    <Td><Toggle checked={!!r.is_active} onChange={(v) => toggleRate(r, v)} /></Td>
                    <Td className="tw-text-right tw-whitespace-nowrap">
                      <Button variant="ghost" size="sm" icon="fa-pen" onClick={() => setRateModal({ ...r, rate: r.type === 'percentage' ? r.rate : r.rate })} />
                      <Button variant="ghost" size="sm" icon="fa-trash" onClick={() => deleteRate(r)} />
                    </Td>
                  </Tr>
                ))}
              </Table>
            )}
          </Card>
          {!taxEnabled && rates.some((r) => r.is_active) && (
            <p className="tw-mt-3 tw-text-xs tw-text-warn"><i className="fa-solid fa-triangle-exclamation tw-mr-1" />Some rates are active but the master switch is off, so nothing is charged.</p>
          )}
        </>
      )}

      <Modal show={!!rateModal} onHide={() => setRateModal(null)} centered>
        <Modal.Header closeButton><Modal.Title>{rateModal?.id ? 'Edit rate' : 'Add rate / charge'}</Modal.Title></Modal.Header>
        <Modal.Body>
          {rateModal && (
            <div className="tw-space-y-4">
              <Field label="Name" required><Input value={rateModal.name} onChange={(e) => setRateModal((r) => ({ ...r, name: e.target.value }))} placeholder="GST / HST / Handling fee" /></Field>
              <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                <Field label="Type">
                  <Select value={rateModal.type} onChange={(e) => setRateModal((r) => ({ ...r, type: e.target.value }))}>
                    <option value="percentage">Percentage of price</option>
                    <option value="fixed">Fixed charge (cents)</option>
                  </Select>
                </Field>
                <Field label={rateModal.type === 'percentage' ? 'Rate (%)' : 'Amount (cents)'}>
                  <Input type="number" step={rateModal.type === 'percentage' ? '0.001' : '1'} value={rateModal.rate} onChange={(e) => setRateModal((r) => ({ ...r, rate: e.target.value }))} />
                </Field>
              </div>
              <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                <Field label="Registration #" hint="e.g. GST/HST number"><Input value={rateModal.registration_number || ''} onChange={(e) => setRateModal((r) => ({ ...r, registration_number: e.target.value }))} /></Field>
                <Field label="Region label" hint="Display only"><Input value={rateModal.region || ''} onChange={(e) => setRateModal((r) => ({ ...r, region: e.target.value }))} placeholder="ON / Canada" /></Field>
              </div>
              <Field label="Description"><Input value={rateModal.description || ''} onChange={(e) => setRateModal((r) => ({ ...r, description: e.target.value }))} /></Field>
              <Toggle label="Active" checked={!!rateModal.is_active} onChange={(v) => setRateModal((r) => ({ ...r, is_active: v }))} />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRateModal(null)}>Cancel</Button>
          <Button onClick={saveRate} disabled={!rateModal?.name}>Save</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BillingTaxSettings;
